import { useState, type ChangeEvent } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { provinceConversionApi } from '../../../../services/provinceConversionApi';

export interface MappingUploadPanelProps {
	onUpload: (file: File) => void;
	isUploading: boolean;
	warnings: string[];
}

export function MappingUploadPanel({ onUpload, isUploading, warnings }: MappingUploadPanelProps) {
	const [fileName, setFileName] = useState<string | null>(null);
	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
	const [downloadError, setDownloadError] = useState<string | null>(null);

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) return;
		setFileName(file.name);
		onUpload(file);
	};

	const handleDownloadTemplate = async () => {
		setDownloadError(null);
		setIsDownloadingTemplate(true);
		try {
			await provinceConversionApi.downloadTemplate();
		} catch {
			setDownloadError('Không tải được file mẫu. Vui lòng thử lại.');
		} finally {
			setIsDownloadingTemplate(false);
		}
	};

	return (
		<Stack spacing={1}>
			<Typography variant="subtitle1">Tải lên ánh xạ</Typography>
			<Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
				<Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={isUploading}>
					Chọn file CSV hoặc Excel
					<input type="file" accept=".csv,.xlsx" hidden onChange={handleFileChange} />
				</Button>
				<Button
					variant="text"
					startIcon={<DownloadIcon />}
					disabled={isDownloadingTemplate}
					onClick={handleDownloadTemplate}
				>
					Tải file mẫu
				</Button>
				{fileName && (
					<Typography variant="body2" color="text.secondary">
						{fileName}
					</Typography>
				)}
			</Box>
			{downloadError && <Alert severity="error">{downloadError}</Alert>}
			{isUploading && <LinearProgress />}
			{warnings.length > 0 && (
				<Alert severity="warning">
					<Stack component="ul" sx={{ m: 0, pl: 2 }}>
						{warnings.map((warning, index) => (
							<Typography component="li" variant="body2" key={index}>
								{warning}
							</Typography>
						))}
					</Stack>
				</Alert>
			)}
			<Typography variant="caption" color="text.secondary">
				Cột bắt buộc: Mã tỉnh cũ, Mã tỉnh mới, Tên tỉnh mới. Tối đa 200 dòng, 1MB.
			</Typography>
		</Stack>
	);
}
